<template>
  <menu-content-container :header="$t('forms.list_settings')">
    <content-row>
      <t-input-number
        v-model="marginTop"
        data-testid="list-margin-top"
        :label="$t('forms.list_margin_top')"
        :regex-pattern="'\d+'"
        :min="min"
        :max="max"
        unit=""
        :changed="marginTopValues?.wasChanged"
        @reset="resetListStyle('margin-top')"
      />
      <t-input-number
        v-model="marginBottom"
        data-testid="list-margin-bottom"
        :label="$t('forms.list_margin_bottom')"
        :regex-pattern="'\d+'"
        :min="min"
        :max="max"
        unit=""
        :changed="marginBottomValues?.wasChanged"
        @reset="resetListStyle('margin-bottom')"
      />
    </content-row>
    <content-row>
      <t-input-number
        v-model="paddingLeft"
        data-testid="list-padding-left"
        :label="$t('forms.list_padding_left')"
        :regex-pattern="'\d+'"
        :min="min"
        :max="max"
        unit=""
        :changed="paddingLeftValues?.wasChanged"
        @reset="resetListStyle('padding-left')"
      />
    </content-row>
  </menu-content-container>
  <menu-content-container :header="$t('forms.settings_list_item')" border-full>
    <font-size-color
      data-testid="font-size-color-li"
      tag="li"
      :label="$t('forms.settings_list_item')"
      class="mt-4"
    ></font-size-color>
    <content-row>
      <TButtonGroup
        :label="$t('forms.list_style_ul')"
        :changed="ulListStyleValues?.wasChanged"
        :reset-tooltip="$t('forms.reset_value')"
        @reset="resetListStyle('list-style-type', 'ul')"
      >
        <TGroupButton
          data="dics"
          small
          :active="ulListStyle === 'disc'"
          @button-clicked="ulListStyle = 'disc'"
        >
          <ListDisc />
        </TGroupButton>
        <TGroupButton
          data="circle"
          small
          :active="ulListStyle === 'circle'"
          @button-clicked="ulListStyle = 'circle'"
        >
          <ListCircle />
        </TGroupButton>
        <TGroupButton
          data="square"
          small
          :active="ulListStyle === 'square'"
          @button-clicked="ulListStyle = 'square'"
        >
          <ListSquare />
        </TGroupButton>
      </TButtonGroup>
    </content-row>
    <content-row>
      <TButtonGroup
        :label="$t('forms.list_style_ol')"
        :changed="olListStyleValues?.wasChanged"
        :reset-tooltip="$t('forms.reset_value')"
        @reset="resetListStyle('list-style-type', 'ol')"
      >
        <TGroupButton
          data="decimal"
          small
          :active="olListStyle === 'decimal'"
          @button-clicked="olListStyle = 'decimal'"
        >
          <ListDecimal />
        </TGroupButton>
        <TGroupButton
          data="lower-alpha"
          small
          :active="olListStyle === 'lower-alpha'"
          @button-clicked="olListStyle = 'lower-alpha'"
        >
          <ListLowerAlpha />
        </TGroupButton>
        <TGroupButton
          data="lower-roman"
          small
          :active="olListStyle === 'lower-roman'"
          @button-clicked="olListStyle = 'lower-roman'"
        >
          <ListLowerRoman />
        </TGroupButton>
        <TGroupButton
          data="lower-greek"
          small
          :active="olListStyle === 'lower-greek'"
          @button-clicked="olListStyle = 'lower-greek'"
        >
          <ListLowerGreek />
        </TGroupButton>
        <TGroupButton
          data="upper-alpha"
          small
          :active="olListStyle === 'upper-alpha'"
          @button-clicked="olListStyle = 'upper-alpha'"
        >
          <ListUpperAlpha />
        </TGroupButton>
        <TGroupButton
          data="upper-roman"
          small
          :active="olListStyle === 'upper-roman'"
          @button-clicked="olListStyle = 'upper-roman'"
        >
          <ListUpperRoman />
        </TGroupButton>
      </TButtonGroup>
    </content-row>
  </menu-content-container>
</template>

<script lang="ts" setup>
  import FontSizeColor from "../FontSizeColor/FontSizeColor.vue";
  import { useMjmlStore } from "@/stores/mjmlStore";

  import {
    ListDisc,
    ListCircle,
    ListSquare,
    ListDecimal,
    ListLowerAlpha,
    ListLowerRoman,
    ListLowerGreek,
    ListUpperAlpha,
    ListUpperRoman,
  } from "@topol/icons";
  import { TInputNumber, TButtonGroup, TGroupButton } from "@topol/twig-ui";
  import { computed } from "vue";
  import type { TopolTag } from "@/schemas/templateSchema";
  import type { TopolTagPropsAttr } from "types/Mjml/IMjmlStore";

  const min = 0;
  const max = 100;

  const mjmlStore = useMjmlStore();

  const marginTopValues = computed(() =>
    mjmlStore.getStyleWithDefault("ul" as TopolTag, "margin-top")
  );
  const marginBottomValues = computed(() =>
    mjmlStore.getStyleWithDefault("ul" as TopolTag, "margin-bottom")
  );
  const paddingLeftValues = computed(() =>
    mjmlStore.getStyleWithDefault("ul" as TopolTag, "padding-left")
  );
  const ulListStyleValues = computed(() =>
    mjmlStore.getStyleWithDefault("ul" as TopolTag, "list-style-type")
  );
  const olListStyleValues = computed(() =>
    mjmlStore.getStyleWithDefault("ol" as TopolTag, "list-style-type")
  );

  function resetListStyle(attr: string, tag?: "ul" | "ol") {
    if (tag) {
      mjmlStore.setStyle({
        tag: tag as TopolTag,
        attr: attr as TopolTagPropsAttr,
        value: undefined,
      });
    } else {
      // Reset for both ul and ol (margin, padding)
      mjmlStore.setStyle({
        tag: "ul" as TopolTag,
        attr: attr as TopolTagPropsAttr,
        value: undefined,
      });
      mjmlStore.setStyle({
        tag: "ol" as TopolTag,
        attr: attr as TopolTagPropsAttr,
        value: undefined,
      });
    }
  }

  const paddingLeft = computed({
    get() {
      return parseInt(mjmlStore.getStyle("ul", "padding-left") || "40px");
    },
    set(val: number) {
      if (val !== null || val !== undefined) {
        mjmlStore.setStyle({
          tag: "ul",
          attr: "padding-left",
          value: val + "px",
        });
        mjmlStore.setStyle({
          tag: "ol",
          attr: "padding-left",
          value: val + "px",
        });
      }
    },
  });

  const marginTop = computed({
    get() {
      return parseInt(mjmlStore.getStyle("ul", "margin-top") || "11px");
    },
    set(val: number) {
      if (val !== null || val !== undefined) {
        mjmlStore.setStyle({
          tag: "ul",
          attr: "margin-top",
          value: val + "px",
        });
        mjmlStore.setStyle({
          tag: "ol",
          attr: "margin-top",
          value: val + "px",
        });
      }
    },
  });

  const marginBottom = computed({
    get() {
      return parseInt(mjmlStore.getStyle("ul", "margin-bottom") || "11px");
    },
    set(val: number) {
      if (val !== null || val !== undefined) {
        mjmlStore.setStyle({
          tag: "ul",
          attr: "margin-bottom",
          value: val + "px",
        });
        mjmlStore.setStyle({
          tag: "ol",
          attr: "margin-bottom",
          value: val + "px",
        });
      }
    },
  });

  const ulListStyle = computed({
    get() {
      return mjmlStore.getStyle("ul", "list-style-type") || "disc";
    },
    set(val: string) {
      mjmlStore.setStyle({
        tag: "ul",
        attr: "list-style-type",
        value: val,
      });
    },
  });

  const olListStyle = computed({
    get() {
      return mjmlStore.getStyle("ol", "list-style-type") || "decimal";
    },
    set(val: string) {
      mjmlStore.setStyle({
        tag: "ol",
        attr: "list-style-type",
        value: val,
      });
    },
  });
</script>
